import { useCallback, useState } from 'react'
import {
  getReturnsChallanList,
  editReturnsChallanList,
} from '../api/returnsStep'
import { returnsChallanRowSchema, RETURNS_CHALLAN_COLUMNS } from '../constants/returnsStep'
import { RETURNS_CHALLAN_COLUMNS_140, RETURNS_CHALLAN_COLUMNS_144, returnsChallanRowSchemaNew } from '../constants/returnsNewFormChallan'
import { isNewFormVersion } from '../utils/formVersionUtils'

/**
 * Returns the appropriate challan columns based on form version and type.
 * @param {string} financialYear - e.g., '2026-27'
 * @param {string} formType - e.g., '26Q', '27Q', '140', '144'
 * @returns {Array} column definitions
 */
export const getChallanColumns = (financialYear, formType) => {
  if (!isNewFormVersion(financialYear)) return RETURNS_CHALLAN_COLUMNS
  if (formType === '144' || formType === '27Q') return RETURNS_CHALLAN_COLUMNS_144
  return RETURNS_CHALLAN_COLUMNS_140
}

export const useReturnsChallan = ({
  tanMasterUuid,
  defaultSearchCOList,
  showSnackbar,
}) => {
  const [challanData, setChallanData] = useState([])
  const [editingData, setEditingData] = useState({})
  const [selectedRows, setSelectedRows] = useState([])

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [challanDataLoading, setChallanDataLoading] = useState(false)

  const fetchChallanData = useCallback(async ({ tanMasterUuid, defaultSearchCOList, currentPage = 1, itemsPerPage = 100 }) => {
    const { entityTan, entityName, returnType, formType, quarter, financialYear } =
      defaultSearchCOList || {}

    setChallanDataLoading(true)

    const payload = {
      pageFilter: {
        includeCount: true,
        currentPage,
        sortField: 'createdDate',
        sortOrder: 'DESC',
        pageSize: itemsPerPage,
      },
      tanUuid: tanMasterUuid,
      entityTAN: entityTan,
      entityName,
      returnType: returnType?.toUpperCase(),
      formType: formType,
      quarter,
      financialYear: financialYear
    }

    const resp = await getReturnsChallanList(payload)

    setChallanData(resp?.data?.content || [])
    setTotalItems(resp?.data?.totalElements || 0)
    setChallanDataLoading(false)
    return true
  }, [tanMasterUuid, defaultSearchCOList, currentPage, itemsPerPage])

  /* ================= EDIT ================= */

  const handleFieldChange = (uuid, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        [field]: value,
      },
    }))
  }

  const clearEdits = () => setEditingData({})


  /* ================= BUILD PAYLOAD ================= */

  const buildSavePayload = () => {
    const entries = Object.entries(editingData)

    if (!entries.length) {
      return { success: true, payload: [] }
    }

    const payload = []
    const errors = []

    // Determine which schema to use based on form version
    const financialYear = defaultSearchCOList?.financialYear
    const useNewSchema = isNewFormVersion(financialYear)
    const schema = useNewSchema ? returnsChallanRowSchemaNew : returnsChallanRowSchema

    for (const [uuid, editedFields] of entries) {
      const row = challanData.find(r => String(r.uuid) === String(uuid))

      if (!row) {
        errors.push(`Edited row not found for uuid ${uuid}`)
        continue
      }

      const rowIndex = challanData.findIndex(
        r => String(r.uuid) === String(uuid)
      )

      const mergedRow = { ...row, ...editedFields }

      const normalized = useNewSchema
        ? {
            ...mergedRow,
            uuid: mergedRow.uuid,
            serialNo: mergedRow.serialNo,
            challanSerialNo: String(mergedRow.challanSerialNo ?? ''),
            bsrCode: String(mergedRow.bsrCode ?? ''),
            dateOfTransferVoucher: String(mergedRow.dateOfTransferVoucher ?? ''),
            totalAmountAsPerChallan: String(mergedRow.totalAmountAsPerChallan ?? ''),
            totalTaxDeducted: String(mergedRow.totalTaxDeducted ?? '0'),
            interest: String(mergedRow.interest ?? '0'),
            fee: String(mergedRow.fee ?? '0'),
            penalty: String(mergedRow.penalty ?? '0'),
            sectionCode: String(mergedRow.sectionCode ?? ''),
            minorHeadOfChallan: String(mergedRow.minorHeadOfChallan ?? ''),
            modeOfDepositThroughBookAdjustment: String(
              mergedRow.modeOfDepositThroughBookAdjustment ?? ''
            ),
            interestAllocated: String(mergedRow.interestAllocated ?? ''),
            others: String(mergedRow.others ?? '')
          }
        : {
            ...mergedRow,
            uuid: mergedRow.uuid,
            serialNo: mergedRow.serialNo,
            challanSerialNo: String(mergedRow.challanSerialNo ?? ''),
            bsrCode: String(mergedRow.bsrCode ?? ''),
            dateOfTransferVoucher: String(mergedRow.dateOfTransferVoucher ?? ''),
            totalAmountAsPerChallan: String(mergedRow.totalAmountAsPerChallan ?? ''),
            tds: String(mergedRow.tds ?? ''),
            surcharge: String(mergedRow.surcharge ?? '0'),
            educationCess: String(mergedRow.educationCess ?? '0'),
            interest: String(mergedRow.interest ?? '0'),
            fee: String(mergedRow.fee ?? '0'),
            penalty: String(mergedRow.penalty ?? '0'),
            others: String(mergedRow.others ?? ''),
            sectionCode: String(mergedRow.sectionCode ?? ''),
            minorHeadOfChallan: String(mergedRow.minorHeadOfChallan ?? ''),
            modeOfDepositThroughBookAdjustment: String(
              mergedRow.modeOfDepositThroughBookAdjustment ?? ''
            ),
            interestAllocated: String(mergedRow.interestAllocated ?? ''),
          }

      const result = schema.safeParse(normalized)

      if (!result.success) {
        result.error.issues.forEach(issue => {
          errors.push(
            `Row ${rowIndex + 1} [${issue.path[0]}]: ${issue.message}`
          )
        })
        continue
      }

      payload.push(normalized)
    }

    if (errors.length) {
      return { success: false, errors, payload: [] }
    }

    return { success: true, payload }
  }

  const saveChallan = async () => {
    const { success, payload, errors } = buildSavePayload()

    if (!success) {
      errors.forEach(msg => showSnackbar(msg, 'error'))
      return false
    }

    if (!payload.length) {
      showSnackbar('No changes to save', 'info')
      return true
    }

    const response = await editReturnsChallanList(payload)

    if (response?.data?.success) {
      showSnackbar('Challan saved successfully', 'success')
      clearEdits()
      await fetchChallanData({ tanMasterUuid, defaultSearchCOList })

      return true
    } else {
      const errMessage = response?.data?.errors?.pop()?.message
      showSnackbar(errMessage || 'Challan save failed', 'error')
      return false
    }
  }

  const handlePageChange = async page => {
    setCurrentPage(page)
    fetchChallanData({ tanMasterUuid, defaultSearchCOList, currentPage: page, itemsPerPage })
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    fetchChallanData({ tanMasterUuid, defaultSearchCOList, currentPage, itemsPerPage: newItemsPerPage })
  }


  return {
    challanData,
    selectedRows,
    totalItems,
    currentPage,
    itemsPerPage,
    editingData,
    challanDataLoading,

    fetchChallanData,
    handleFieldChange,
    saveChallan,
    handlePageChange,
    handleItemsPerPageChange,
    setSelectedRows,
    setCurrentPage,
    setItemsPerPage,
    clearEdits,
  }
}
